import { useEffect, useState } from "react";
import { Bell, ChevronDown } from "lucide-react";
import { dayjs } from "@boo/shared";
import {
  getPublicNotifications,
  markPublicNotificationsRead,
  type PublicNotification,
} from "@/services/publicBillApi";

interface Props {
  token: string;
  personId: string;
  /** Changes whenever the bill does; triggers a refetch. */
  revision: string;
}

/** Collapsible list of what happened on this bill, for the friend. */
export default function UpdatesStrip({ token, personId, revision }: Props) {
  const [items, setItems] = useState<PublicNotification[]>([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    async function loadUpdates() {
      try {
        const res = await getPublicNotifications(token, personId);
        if (cancelled) return;
        setItems(res.items);
        setUnread(res.unread);
      } catch {
        // updates are secondary: keep what we have
      }
    }
    void loadUpdates();
    return () => {
      cancelled = true;
    };
  }, [token, personId, revision]);

  function toggle() {
    const next = !open;
    setOpen(next);
    if (next && unread > 0) {
      setUnread(0);
      void (async () => {
        try {
          await markPublicNotificationsRead(token, personId);
        } catch {
          // best effort
        }
      })();
    }
  }

  if (items.length === 0) return null;

  return (
    <section className="mb-4 rounded-xl border border-border bg-card">
      <button
        type="button"
        onClick={toggle}
        aria-expanded={open}
        className="flex w-full items-center justify-between gap-2 px-4 py-2.5 text-sm font-medium text-foreground"
      >
        <span className="inline-flex items-center gap-2">
          <Bell className="w-4 h-4 text-muted-foreground" />
          Updates
          {unread > 0 && (
            <span className="rounded-full bg-primary px-1.5 text-[11px] font-semibold text-primary-foreground">
              {unread}
            </span>
          )}
        </span>
        <ChevronDown
          className={`w-4 h-4 text-muted-foreground transition-transform ${
            open ? "rotate-180" : ""
          }`}
        />
      </button>
      {open && (
        <ul className="divide-y divide-border border-t border-border">
          {items.map((n) => (
            <li key={n.id} className="px-4 py-2">
              <div className="text-sm text-foreground">{n.text}</div>
              <div className="text-[11px] text-muted-foreground">
                {dayjs(n.createdAt).format("D MMM HH:mm")}
              </div>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
